import { useState, useEffect, useMemo } from 'react';
import { Search, Filter, Package } from 'lucide-react';
import { productsApi, BACKEND_URL } from '../services/api';
import ProductCard from '../components/cards/ProductCard';
import LoadingSpinner from '../components/common/LoadingSpinner';
import EmptyState from '../components/common/EmptyState';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const ProductPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const toImageUrl = (value) => {
    if (!value || typeof value !== 'string') return '/placeholder.png';
    if (value.startsWith('http://') || value.startsWith('https://')) return value;
    if (value.startsWith('data:image')) return value;
    return `${BACKEND_URL}${value.startsWith('/') ? value : `/${value}`}`;
  };

  const extractProducts = (responseData) => {
    if (Array.isArray(responseData)) return responseData;
    if (Array.isArray(responseData?.data)) return responseData.data;
    if (Array.isArray(responseData?.products)) return responseData.products;
    if (Array.isArray(responseData?.cartItems)) return responseData.cartItems;
    if (Array.isArray(responseData?.items)) return responseData.items;
    return [];
  };

  const normalizeProduct = (product, index = 0) => {
    const gallery = Array.isArray(product?.images)
      ? product.images
          .map((item) => {
            if (typeof item === 'string') return { img: item };
            if (item && typeof item === 'object' && item.img) return { img: item.img };
            return null;
          })
          .filter(Boolean)
      : [];

    return {
      _id: product?._id || product?.id || String(index),
      productName: product?.productName || '',
      productType: product?.productType || '',
      productPrice: Number(product?.productPrice || 0),
      productCategory: product?.productCategory || '',
      mainImg: product?.mainImg || '',
      images: gallery,
      createdAt: product?.createdAt,
      updatedAt: product?.updatedAt,
    };
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await productsApi.getAll();
      const rawProducts = extractProducts(response?.data);
      const normalizedProducts = rawProducts.map((product, index) =>
        normalizeProduct(product, index)
      );

      setProducts(normalizedProducts);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Məhsulları yükləmək mümkün olmadı.');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const categories = useMemo(() => {
    return [
      ...new Set(
        products
          .map((product) => product.productCategory?.trim())
          .filter(Boolean)
      ),
    ];
  }, [products]);

  const filteredProducts = useMemo(() => {
    let result = [...products];
    const search = searchTerm.trim().toLowerCase();

    if (search) {
      result = result.filter((product) => {
        const name = product.productName.toLowerCase();
        const type = product.productType.toLowerCase();
        const category = product.productCategory.toLowerCase();

        return (
          name.includes(search) ||
          type.includes(search) ||
          category.includes(search)
        );
      });
    }

    if (selectedCategory !== 'all') {
      result = result.filter(
        (product) =>
          product.productCategory.trim().toLowerCase() ===
          selectedCategory.trim().toLowerCase()
      );
    }

    return result;
  }, [products, searchTerm, selectedCategory]);

  return (
    <div className="min-h-screen bg-brand-cream/30" data-testid="products-page">
      {/* Hero */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-brand-cream via-brand-accent/20 to-brand-secondary/30 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-brand-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-brand-gold/10 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center relative z-10">
          <span className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm text-brand-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Package className="w-4 h-4" />
            Kolleksiya
          </span>

          <h1 className="font-serif text-4xl md:text-6xl text-brand-brown">
            Bütün <span className="text-brand-primary italic">Məhsullar</span>
          </h1>

          <p className="mt-4 text-brand-muted max-w-xl mx-auto text-lg">
            Ən yeni və unikal məhsullarımızı kəşf edin
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 bg-white border-b border-brand-secondary/30 sticky top-20 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-muted" />
              <Input
                type="text"
                placeholder="Məhsul axtar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 rounded-full border-brand-secondary bg-brand-cream/50 focus:border-brand-primary"
                data-testid="products-search-input"
              />
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-2 text-brand-muted">
                <Filter className="w-4 h-4" />
                <span className="text-sm font-medium">Kateqoriya:</span>
              </div>

              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger
                  className="w-full sm:w-[220px] rounded-full border-brand-secondary"
                  data-testid="products-category-filter"
                >
                  <SelectValue placeholder="Hamısı" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Hamısı</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <p className="text-sm text-brand-muted bg-brand-accent/30 px-4 py-2 rounded-full whitespace-nowrap">
                {filteredProducts.length} məhsul tapıldı
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <LoadingSpinner text="Məhsullar yüklənir..." />
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-red-500">{error}</p>
              <button
                onClick={fetchProducts}
                className="mt-4 text-sm text-brand-primary underline"
                data-testid="products-retry-button"
              >
                Yenidən cəhd et
              </button>
            </div>
          ) : filteredProducts.length === 0 ? (
            <EmptyState
              icon={Package}
              title="Məhsul tapılmadı"
              description={
                searchTerm || selectedCategory !== 'all'
                  ? 'Axtarış meyarlarınızı dəyişin.'
                  : 'Hazırda göstəriləcək məhsul yoxdur.'
              }
            />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProducts.map((product, index) => (
                <ProductCard
                  key={product._id || index}
                  product={product}
                  index={index}
                  toImageUrl={toImageUrl}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ProductPage;